import { useEffect, useState } from 'react';
import { buildRecurrencePreset } from '../api';

/**
 * Choosing how often a template repeats.
 *
 * The rule has been storable since Phase 1b and settable from nowhere: the template form never
 * offered it, so the only way to make a template recur was the API or MCP — and until this phase
 * nothing acted on the rule even then.
 *
 * Presets rather than a free-text RRULE. "FREQ=MONTHLY;BYMONTHDAY=1" is a thing a machine writes;
 * the API still accepts any valid rule for callers that want one, which is the right split
 * (§7.3): the UI is opinionated, the interface is not.
 */

const PRESET_LABELS: Record<string, string> = {
  daily: 'Every day',
  weekdays: 'Every weekday',
  weekly: 'Every week',
  every_n_weeks: 'Every few weeks',
  monthly_on_date: 'Monthly, on a date',
};

export function RecurrencePicker({
  value,
  onChange,
}: {
  /** The current RRULE, or empty for "does not repeat". */
  value: string;
  onChange: (rule: string) => void;
}) {
  const [preset, setPreset] = useState('');
  const [interval, setInterval] = useState('2');
  const [monthDay, setMonthDay] = useState('1');
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether the user has chosen anything yet.
   *
   * An existing rule cannot be mapped back to the preset that produced it, so the control opens
   * on "keep it" rather than guessing. Without this the effect below fired on mount with no
   * preset selected and reported an empty rule — silently clearing the schedule of any template
   * whose edit form was merely *opened*. The worst kind of data loss: invisible, and caused by
   * looking rather than by changing anything.
   */
  const [touched, setTouched] = useState(false);
  const hasExisting = value !== '';

  // The server builds the rule and describes it, so the UI never has to know RRULE syntax and
  // the description shown is the one the API would give back.
  useEffect(() => {
    // Until something is chosen, leave whatever the template already has alone.
    if (!touched) return;
    if (preset === '') {
      onChange('');
      setText(null);
      return;
    }
    let cancelled = false;
    buildRecurrencePreset({
      preset,
      ...(preset === 'every_n_weeks' ? { interval: Number(interval) || 2 } : {}),
      ...(preset === 'monthly_on_date' ? { monthDay: Number(monthDay) || 1 } : {}),
    })
      .then((result) => {
        if (cancelled) return;
        setError(null);
        setText(result.text);
        onChange(result.rule);
      })
      .catch(() => {
        if (!cancelled) setError('Could not build that schedule.');
      });
    return () => {
      cancelled = true;
    };
    // Only the inputs that define the rule. `onChange` is a fresh closure on every render, so
    // including it would re-run this effect endlessly. (No eslint directive: the
    // exhaustive-deps rule is not configured in this project, and referencing a rule that does
    // not exist is itself an error.)
  }, [preset, interval, monthDay, touched]);

  return (
    <>
      <label>
        Repeats
        <select
          value={touched || !hasExisting ? preset : 'keep'}
          aria-label="Repeats"
          onChange={(e) => {
            setTouched(true);
            setPreset(e.target.value === 'keep' ? '' : e.target.value);
          }}
        >
          {hasExisting && !touched && <option value="keep">Keep the current schedule</option>}
          <option value="">Does not repeat</option>
          {Object.entries(PRESET_LABELS).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </label>

      {preset === 'every_n_weeks' && (
        <label>
          Every how many weeks
          <input
            type="number"
            min={1}
            max={52}
            value={interval}
            aria-label="Week interval"
            onChange={(e) => setInterval(e.target.value)}
          />
        </label>
      )}

      {preset === 'monthly_on_date' && (
        <label>
          Day of the month
          <input
            type="number"
            min={1}
            max={31}
            value={monthDay}
            aria-label="Day of the month"
            onChange={(e) => setMonthDay(e.target.value)}
          />
        </label>
      )}

      {/* The plain-language rendering, so nobody has to trust an RRULE they cannot read. */}
      {text && (
        <p className="subtext" data-testid="recurrence-text">
          {text} — a task will be created automatically each time.
        </p>
      )}
      {hasExisting && !touched && (
        <p className="subtext" data-testid="recurrence-existing">
          This template already repeats — a task is created automatically each time.
        </p>
      )}
      {!hasExisting && !touched && (
        <p className="subtext">Repeating templates create their task on their own.</p>
      )}
      {error && <p className="error">{error}</p>}
    </>
  );
}
